import React, { useCallback, useEffect, useState } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import type { AlertProps } from '@mui/material/Alert';
import {
    Stack,
    Snackbar,
    TextField,
    Grid,
    Typography,
    Alert,
    Button,
    MenuItem,
    Select,
    FormControl,
    Tooltip,
    IconButton,
    Divider,
    Menu,
    useMediaQuery,
    DialogActions,
    DialogTitle,
    Dialog,
    DialogContent,
    Box
} from '@mui/material';
import { Breadcrumb } from 'components';
import PreviewIcon from '@mui/icons-material/Preview';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import moment from 'moment';
import { DataGrid, GridRenderCellParams } from '@mui/x-data-grid';
import {
    viewAllQuote,
    quotePermissionCheck,
    deleteQuote,
    shareWithCustomerApi,
    generatePdfFile,
    getQuoteStatus,
    updateQuoteStatus,
    cloneQuoteApi,
    newVersionApi
} from 'services';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import IosShareOutlinedIcon from '@mui/icons-material/IosShareOutlined';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import PageviewOutlinedIcon from '@mui/icons-material/PageviewOutlined';
import MarkUnreadChatAltOutlinedIcon from '@mui/icons-material/MarkUnreadChatAltOutlined';
import PostAddIcon from '@mui/icons-material/PostAdd';
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import ContentPasteSearchOutlinedIcon from '@mui/icons-material/ContentPasteSearchOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { createSearchParams, useNavigate } from 'react-router';
import CloseIcon from '@mui/icons-material/Close';
import MoreHorizRoundedIcon from '@mui/icons-material/MoreHorizRounded';
import { ShareWithCustomer } from '../components/ShareWithCustomer';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import { SharedQuotes } from '../module/ViewQuotes/SharedQuotes';
import { CommentsGrid } from '../components/CommentsGrid';
import useFetch from '../../customHooks/useFetch';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialogContent-root': {
        padding: theme.spacing(2),
    },
    '& .MuiDialogActions-root': {
        padding: theme.spacing(1),
    },
}));
export interface DialogTitleProps3 {
    id: string;
    children?: React.ReactNode;
    onClose: () => void;
}

const BootstrapDialogTitle = (props: DialogTitleProps3) => {
    const { children, onClose, ...other } = props;
    return (
        <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
            {children}
            {onClose ? (
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            ) : null}
        </DialogTitle>
    );
}

interface QuoteConfirmationDialogProps {
    fullScreen: boolean;
    title: React.ReactNode;
    commentLabel?: string;
    initialComment: string;
    onConfirm: (comment: string) => void;
    onClose: () => void;
    onCancel: () => void;
}

const QuoteConfirmationDialog = ({
    fullScreen, title, commentLabel, initialComment, onConfirm, onClose, onCancel
}: QuoteConfirmationDialogProps) => {
    const [comment, setComment] = useState(initialComment);

    return (
        <BootstrapDialog onClose={onClose} open fullScreen={fullScreen} fullWidth sx={{ padding: 10 }}>
            <BootstrapDialogTitle id="customized-dialog-title" onClose={onClose}>
                <Typography component="span" variant="h6" sx={{ fontWeight: 400 }}>
                    {title}
                </Typography>
            </BootstrapDialogTitle>
            {commentLabel && (
                <DialogContent style={{ padding: 20 }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={12} md={12} style={{ paddingTop: 0 }}>
                            <Typography variant="subtitle1" sx={{ paddingBottom: '10px', fontWeight: 'bold' }}>
                                {commentLabel}<span style={{ color: '#f00' }}> *</span>
                            </Typography>
                            <TextField
                                id="outlined-multiline-static"
                                sx={{ width: "100%" }}
                                multiline
                                rows={4}
                                required
                                value={comment}
                                onChange={(event) => setComment(event.target.value)}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
            )}
            <DialogActions>
                <Button variant="contained" color="primary" onClick={() => onConfirm(comment)} disabled={!!commentLabel && !comment.trim()}>
                    Yes
                </Button>
                <Button variant="outlined" color="primary" onClick={onCancel}>No</Button>
            </DialogActions>
        </BootstrapDialog>
    );
};

let deleteQuoteId: any;
let clearStatus: any = false;
let shareWithCustomer: any;

interface QuoteSearchFormProps {
    onSearch: (values: { searchValue: string; projectName: string; quoteNumber: string; createdBy: string }) => void;
    onClear: () => void;
}

const QuoteSearchForm = React.memo(({ onSearch, onClear }: QuoteSearchFormProps) => {
    const [searchValue, setSearchValue] = useState('');
    const [projectName, setProjectName] = useState('');
    const [quoteNumber, setQuoteNumber] = useState('');
    const [createdBy, setCreatedBy] = useState('');

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        e.stopPropagation();
        onSearch({ searchValue, projectName, quoteNumber, createdBy });
    };

    const handleClear = () => {
        setSearchValue('');
        setProjectName('');
        setQuoteNumber('');
        setCreatedBy('');
        onClear();
    };

    return (
        <form onSubmit={handleSubmit}>
            <Grid container spacing={2} mt={1} mb={3} style={{ backgroundColor: '#f5f5f5', marginLeft: '0px', padding: '16px', borderRadius: '6px', width: '100%' }}>
                <Grid item xs={3} sm={6} md={3} style={{ padding: '0px 8px' }}>
                    <TextField
                        id="outlined-basic"
                        sx={{ width: '100%', '& .MuiFormLabel-root': { fontSize: '12px' } }}
                        type="text"
                        label="Search Client"
                        value={searchValue}
                        onChange={(ev) => setSearchValue(ev.target.value)}
                        size="small"
                    />
                </Grid>
                <Grid item xs={3} sm={6} md={3} style={{ padding: '0px 8px' }}>
                    <TextField
                        id="outlined-basic-project"
                        sx={{ width: '100%', '& .MuiFormLabel-root': { fontSize: '12px' } }}
                        type="text"
                        label="Project Name"
                        value={projectName}
                        onChange={(ev) => setProjectName(ev.target.value)}
                        size="small"
                    />
                </Grid>
                <Grid item xs={3} sm={6} md={3} style={{ padding: '0px 8px' }} sx={{ mt: { xs: 2, sm: 1, md: 0 } }}>
                    <TextField
                        id="outlined-basic-quote"
                        sx={{ width: '100%', '& .MuiFormLabel-root': { fontSize: '12px' } }}
                        type="text"
                        label="Quote Number"
                        value={quoteNumber}
                        onChange={(ev) => setQuoteNumber(ev.target.value)}
                        size="small"
                    />
                </Grid>
                <Grid item xs={3} sm={6} md={3} style={{ padding: '0px 8px' }} sx={{ mt: { xs: 2, sm: 1, md: 0 } }}>
                    <TextField
                        id="outlined-basic-created-by"
                        sx={{ width: '100%', '& .MuiFormLabel-root': { fontSize: '12px' } }}
                        type="text"
                        label="Created By"
                        value={createdBy}
                        onChange={(ev) => setCreatedBy(ev.target.value)}
                        size="small"
                    />
                </Grid>
                <Grid item xs={3} sm={6} md={3} style={{ padding: '0px 8px' }} sx={{ mt: { xs: 2, sm: 1, md: 1 } }}>
                    <Button variant="contained" type="submit" color="primary" startIcon={<ContentPasteSearchOutlinedIcon />}>
                        Search
                    </Button>
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={handleClear}
                        startIcon={<ClearOutlinedIcon />}
                        sx={{ marginLeft: '8px' }}
                    >
                        Clear All
                    </Button>
                </Grid>
            </Grid>
        </form>
    );
});

export const ViewQuote = () => {
    const [confirmAction, setConfirmAction] = useState<'approve' | 'reject' | 'delete' | 'clone'>('delete');
    const [pendingCloneItem, setPendingCloneItem] = useState<any>(null);
    const [pendingCloneType, setPendingCloneType] = useState<'clone' | 'version' | ''>('');
    const [statusQuoteList, setStatusQuoteList] = useState([]);
    const [commentList, setCommentList] = useState([]);
    const [selectedItem, setSelectedItem] = useState<any>();
    const [statusObj, setStatusObj] = useState<any>();
    const [searchValue, setSearchValue] = useState('');
    const [projectName, setProjectName] = useState('');
    const [quoteNumber, setQuoteNumber] = useState('');
    const [createdBy, setCreatedBy] = useState('');
    const [commentsValue, setCommentsValue] = useState('');
    const [sharedQuoteNumber, setSharedQuoteNumber] = useState('');
    const [deleteQuoteStatus, setDeleteQuoteStatus] = useState(false);
    const [internalError, setInternalError] = useState(false);
    const [standardQuote, setStandardQuote] = useState(false);
    const [customQuote, setCustomQuote] = useState(false);
    const [quoteApprover, setQuoteApprover] = useState(false);
    const [duplicateError, setDuplicateError] = useState(false);
    const [quoteNotExist, setQuoteNotExist] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [isShareWithCustomer, setIsShareWithCustomer] = useState(false);
    const [isSuccessMsg, setIsSuccessMsg] = useState(false);
    const [isSharedQuoteEnable, setIsSharedQuoteEnable] = useState(false);
    const [successStatusUpdate, setSuccessStatusUpdate] = useState<{
        type: AlertProps['severity'];
        message: string;
        open: boolean;
    }>({
        type: 'success',
        message: '',
        open: false,
    });
    const [confirmPopup, setConfirmPopup] = useState(false);
    const [isShowAllPopup, setIsShowAllPopup] = useState(false);
    const [currentRowItem, setCurrentRowItem] = useState(false);
    const [downloadSuccess, setDownloadSuccess] = useState(false);
    const [openComments, setOpenComments] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const open = Boolean(anchorEl);
    const [userInfo] = useFetch();
    const [paginationModel, setPaginationModel] = useState({
        page: 1,
        pageSize: 10,
        total: 0,
        data: [],
        isLoading: false
    });
    const [commonError, setCommonError] = useState({
        message: '',
        open: false
    });
    const [menuKey, setMenuKey] = useState(0);

    const handleChange = (event: any, items: any) => {
        let newObj = {
            "quoteId": items?.id,
            "status": event.target.value,
            "quoteNumber": items?.quoteNumber
        }
        if (Number(event.target.value) === 3) {
            setConfirmAction('approve');
            setConfirmPopup(true);
            setConfirmOpen(true);
        } else if (Number(event.target.value) === 4) {
            setConfirmAction('reject');
            setConfirmPopup(true);
            setConfirmOpen(true);
        } else {
            updateStatus(newObj, 'isApprove');
        }
        setStatusObj(newObj);
    };

    const updateStatus = (itemObj: any, type: any, comment = commentsValue) => {
        let Obj: any;
        if (type === 'isApprove') {
            Obj = {
                "quoteID": itemObj?.quoteId,
                "status": itemObj?.status,
            }
        } else {
            Obj = {
                "quoteID": itemObj?.quoteId,
                "status": itemObj?.status,
                "quotesComment": {
                    "quoteID": itemObj?.quoteId,
                    "comment": comment
                }
            }
        }

        updateQuoteStatus(Obj).then((res: any) => {
            if (res.status === 200) {
                setSuccessStatusUpdate({
                    type: 'success',
                    message: res.data || res.data.message,
                    open: true
                });
                pageInitialLoad();
                setStatusObj('');
                console.log(res);
            }
        });
    }

    const handleClick = (event: React.MouseEvent<HTMLElement>, item: any) => {
        setAnchorEl(event.currentTarget);
        setSelectedItem(item);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setSelectedItem([]);
    };

    let navigate = useNavigate();

    const jwt: any = localStorage.getItem('token');
    const labUserCheck: any = jwt ? JSON.parse(atob(jwt.split('.')[1])) : null;

    const permissionCheck = (val: any) => {
        switch (val) {
            case 'StandardQuotes':
                setStandardQuote(true);
                break;
            case 'CustomQuotes':
                setCustomQuote(true)
                break;
            case 'QuotesApprover':
                setQuoteApprover(true)
                break;
            default:
                break;
        }
    }

    useEffect(() => {
        if (labUserCheck && labUserCheck.Permission) {
            if (Array.isArray(labUserCheck.Permission)) {
                labUserCheck.Permission.map((item: any) => {
                    permissionCheck(item);
                })
            }
            else {
                permissionCheck(labUserCheck.Permission);
            }
        }
    }, [labUserCheck.Permission])

    useEffect(() => {
        if (paginationModel.page) {
            pageInitialLoad();
        }
    }, [paginationModel.page])

    const pageInitialLoad = (name = searchValue, pName = projectName, number = quoteNumber, createdByName = createdBy) => {
        if (clearStatus) { name = ''; pName = ''; number = ''; createdByName = ''; }
        setPaginationModel(old => ({ ...old, isLoading: true }));
        viewAllQuote(paginationModel.page, name, pName, number, createdByName).then((res: any) => {
            if (res.status === 200) {
                if (res.data.length > 0) {
                    res.data.map((item: any) => {
                        setPaginationModel(old => ({ ...old, isLoading: false, data: res.data, total: item.totalRecordsCount }))
                    })
                } else {
                    setPaginationModel(old => ({ ...old, isLoading: false, data: [], total: 0 }));
                }
            }
        });
    }

    useEffect(() => {
        initialQuoteStatus();
    }, [])

    const initialQuoteStatus = () => {
        getQuoteStatus().then((res: any) => {
            if (res.status === 200) {
                setStatusQuoteList(res.data);
            }
        });
    }

    const handleSearch = useCallback(({ searchValue: sv, projectName: pn, quoteNumber: qn, createdBy: cb }: { searchValue: string; projectName: string; quoteNumber: string; createdBy: string }) => {
        clearStatus = false;
        setSearchValue(sv);
        setProjectName(pn);
        setQuoteNumber(qn);
        setCreatedBy(cb);
        pageInitialLoad(sv, pn, qn, cb);
    }, []);

    const handleClear = useCallback(() => {
        clearStatus = true;
        setSearchValue('');
        setProjectName('');
        setQuoteNumber('');
        setCreatedBy('');
        pageInitialLoad('', '', '', '');
        setPaginationModel(old => ({ ...old, page: 1 }));
    }, []);

    const pagePreview = (quoteId: any, name: any) => {
        if (name === 'Preview') {
            navigate({
                pathname: "/home/reviewQuote",
                search: createSearchParams({
                    id: quoteId
                }).toString()
            });
        }
    }

    const pagePermissionCheck = (item: any, name: any) => {
        quotePermissionCheck(item?.id).then((res: any) => {
            if (res.status === 200) {
                if (name === 'Preview') {
                    navigate({
                        pathname: "/home/reviewQuote",
                        search: createSearchParams({
                            id: item?.id
                        }).toString()
                    });
                } else if (name === 'Edit') {
                    navigate({
                        pathname: "/home/generateQuote",
                        search: createSearchParams({
                            id: item?.id,
                            status: 'true',
                            client: ((item?.clientID === -1) ? 'new' : 'exist')
                        }).toString()
                    });
                }
            } else if (res.status === 401) {
                setDuplicateError(true);
            }
        })
    }

    const submitClick = (comment: string) => {
        if (confirmAction === 'approve') {
            setCommentsValue(comment);
            updateStatus(statusObj, 'approve', comment);
            setConfirmOpen(false);
            return;
        }

        if (confirmAction === 'reject') {
            updateStatus(statusObj, 'reject', comment);
            setConfirmOpen(false);
            return;
        }

        if (confirmAction === 'clone') {
            handleCloneQuotes(pendingCloneItem, pendingCloneType);
            setConfirmOpen(false);
            return;
        }

        if (confirmAction === 'delete') {
            handleDeleteQuote();
        }
    }

    const handleDeleteQuote = () => {
        deleteQuote(deleteQuoteId).then((res: any) => {
            if (res.status === 200) {
                pageInitialLoad();
                setDeleteQuoteStatus(true);
                setQuoteNotExist(false);
                setConfirmOpen(false);
                setAnchorEl(null);
            } else if (res.status === 400) {
                setQuoteNotExist(true);
                setConfirmOpen(false);
            } else if (res.status === 500) {
                setInternalError(true);
                setConfirmOpen(false);
            }
        })
    }

    const handleShareWithCustomer = (content: any) => {
        shareWithCustomerApi(content).then((res: any) => {
            if (res.status === 200) {
                setIsSuccessMsg(true);
                setIsShareWithCustomer(false);
                pageInitialLoad();
            } else {
                setIsShareWithCustomer(true);
            }
        })
    }

    const handleDeleteConfirm = (quoteId: any) => {
        deleteQuoteId = quoteId;
        setConfirmAction('delete');
        setConfirmOpen(true);
        setConfirmPopup(false);
    }

    const handleGeneratePdf = (val: any) => {
        setAnchorEl(null);
        if (val?.id) {
            generatePdfFile(val.id)
                .then((res: any) => {
                    if (res.status === 200) {
                        setDownloadSuccess(true);
                        const linkSource = `data:application/pdf;base64,${res.data}`;
                        const downloadLink = document.createElement("a");
                        let quoteNo = val.quoteNumber;
                        const fileName = `${quoteNo}.pdf`;
                        downloadLink.href = linkSource;
                        downloadLink.download = fileName;
                        downloadLink.click();
                    } else if (res.status === 400) {
                        setCommonError({
                            message: res.data || 'Something went wrong while generating the PDF.',
                            open: true
                        });
                    }
                }).catch((error: any) => {
                    setCommonError({
                        message:
                            error?.response?.data ||
                            error?.message ||
                            "Something went wrong while generating the PDF.",
                        open: true,
                    });
                })
        }
    }

    const dateRender = (props: GridRenderCellParams<Date>) => {
        const { row } = props;
        return (
            <div>
                {!row.createdOn ? '' : moment(row.createdOn).format("DD/MM/YYYY")}
            </div>
        );
    };

    const renderClientName = (props: any) => {
        const { row } = props;
        return (
            <div style={{ overflowX: "scroll" }}>
                <Tooltip title={row.clientName}><span>{row.clientName}</span></Tooltip>
            </div>
        )
    }

    const renderProjectName = (props: any) => {
        const { row } = props;
        return (
            <div style={{ overflowX: "scroll" }}>
                <Tooltip title={row.projectName}><span>{row.projectName}</span></Tooltip>
            </div>
        )
    }

    const renderQuoteNumber = (props: any) => {
        const { row } = props;
        return (
            <Tooltip title={row.quoteNumber}><span>{row.quoteNumber}</span></Tooltip>
        )
    }

    const renderCreatedByName = (props: any) => {
        const { row } = props;
        return (
            <Tooltip title={row.createdByName}><span>{row.createdByName}</span></Tooltip>
        )
    }

    const statusRender = (props: any) => {
        const { row } = props;
        let statusItems: any = [];
        statusQuoteList.map((el: any) => {
            let obj = {
                id: el.id,
                name: el.name,
                disabled: row?.status === 3
            }
            statusItems.push(obj)
        })
        return (
            <FormControl sx={{ m: 1, minWidth: 158 }}>
                <Select
                    labelId="demo-simple-select-outlined-label"
                    id="demo-simple-select-outlined"
                    onChange={(e: any) => handleChange(e, row)}
                    autoWidth
                    size="small"
                    value={row?.status}
                    disabled={row?.status === 4}
                >
                    {statusItems?.map((i: any) => {
                        return <MenuItem value={i?.id} disabled={i?.disabled}>{i?.name}</MenuItem>
                    })}
                </Select>
            </FormControl>
        )
    }

    const handleSharedQuotes = (val: any) => {
        setIsSharedQuoteEnable(true);
        setIsShowAllPopup(false);
        setAnchorEl(null);
        setSharedQuoteNumber(val);
    }

    const handleShareCustomer = () => {
        setIsShareWithCustomer(true);
        handleClose();
    }

    const handleCloneQuotes = (item: any, name: any) => {
        setAnchorEl(null);
        let selectApi = name === 'clone' ? cloneQuoteApi : newVersionApi;
        selectApi(item?.id).then((res: any) => {
            if (res.status === 200) {
                let obj = {
                    id: res.data,
                    clientID: item.clientID
                }
                pagePermissionCheck(obj, 'Edit');
            }
        });
    }

    const handleCloneConfirm = (item: any, name: 'clone' | 'version') => {
        setPendingCloneItem(item);
        setPendingCloneType(name);
        setConfirmAction('clone');
        setConfirmPopup(false);
        setConfirmOpen(true);
    }

    const handleV2Quote = (item: any) => {
        navigate(`/home/v2-quote?id=${item?.id}`);
    }

    const handleShowVersion = (item: any) => {
        setIsShowAllPopup(true);
        setIsSharedQuoteEnable(true);
        setAnchorEl(null);
        setCurrentRowItem(item)
    }

    const isProd = window.location.origin.includes('elvisconnect.eurofinsanz.com');

    const handleAction = (props: any) => {
        const { row } = props;
        return (
            <div style={{ width: '100%', display: 'inline-flex', padding: '8px' }}>
                <Tooltip title="Action">
                    <IconButton
                        onClick={(e: any) => {
                            handleClick(e, row);
                            shareWithCustomer = row;
                        }}
                        size="small"
                        sx={{ ml: 2 }}
                        aria-controls="menu-appbar1"
                        aria-haspopup="true"
                        aria-expanded={open ? 'true' : undefined}
                    >
                        <MoreHorizRoundedIcon />
                    </IconButton>
                </Tooltip>
                <Menu
                    id="menu-appbar1"
                    key={menuKey}
                    anchorEl={anchorEl}
                    keepMounted
                    PaperProps={{
                        elevation: 0,
                        sx: {
                            overflow: 'visible',
                            filter: 'drop-shadow(0px 2px 2px rgba(217,217,217))',
                            mt: 1.5,
                            '& .MuiAvatar-root': {
                                width: 32,
                                height: 32,
                                ml: -0.5,
                                mr: 1,
                            },
                            '&:before': {
                                content: '""',
                                display: 'block',
                                position: 'absolute',
                                top: 0,
                                right: 14,
                                width: 10,
                                height: 10,
                                bgcolor: 'background.paper',
                                transform: 'translateY(-50%) rotate(45deg)',
                                zIndex: 0,
                            },
                        },
                    }}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                    open={Boolean(anchorEl)}
                    onClose={handleClose}
                >
                    <MenuItem onClick={() => pagePreview(selectedItem?.id, 'Preview')}>
                        <PreviewIcon color="primary" />&nbsp; View
                    </MenuItem>
                    <MenuItem
                        onClick={() => pagePermissionCheck(selectedItem, 'Edit')}
                        disabled={!canEditQuote(selectedItem)}
                    >
                        <EditOutlinedIcon color="primary" />&nbsp; Edit
                    </MenuItem>
                    <MenuItem
                        onClick={() => handleDeleteConfirm(selectedItem?.id)}
                        disabled={!(userInfo && (selectedItem?.status === 1))}
                    >
                        <DeleteOutlinedIcon color="primary" />&nbsp; Delete
                    </MenuItem>
                    <Divider />
                    <MenuItem onClick={() => handleGeneratePdf(selectedItem)}>
                        <ArticleOutlinedIcon color="primary" />&nbsp; Generate Quote
                    </MenuItem>
                    <MenuItem onClick={() => handleShareCustomer()} disabled={isProd}>
                        <IosShareOutlinedIcon color="primary" />&nbsp; Share with customer
                    </MenuItem>
                    <MenuItem onClick={() => handleSharedQuotes(selectedItem)}>
                        <FolderSharedIcon color="primary" />&nbsp; Shared Quotes
                    </MenuItem>
                    <MenuItem
                        onClick={() => handleCloneConfirm(selectedItem, 'clone')}
                    >
                        <FileCopyOutlinedIcon color="primary" />&nbsp; Clone
                    </MenuItem>
                    <MenuItem
                        onClick={() => handleCloneConfirm(selectedItem, 'version')}
                        disabled={(userInfo && (selectedItem?.status === 3))}
                    >
                        <PostAddIcon color="primary" />&nbsp; Create New Revision
                    </MenuItem>
                    <MenuItem
                        onClick={() => handleV2Quote(selectedItem)}
                        disabled={!(userInfo && (selectedItem?.status === 3 && selectedItem?.isV2Quote === false))}
                    >
                        <DescriptionOutlinedIcon color="primary" />&nbsp; V2 Quote
                    </MenuItem>
                    <MenuItem onClick={() => handleShowVersion(selectedItem)}>
                        <PageviewOutlinedIcon color="primary" />&nbsp; Show All Revisions
                    </MenuItem>
                </Menu>
            </div>
        );
    };

    const handleComment = (props: any) => {        const { row } = props;
        return (
            <>
                {row?.quotesComments?.length > 0 ?
                    <Tooltip title="Comments">
                        <IconButton
                            onClick={() => {
                                setOpenComments(true);
                                setCommentList(row);
                            }}
                        >
                            <MarkUnreadChatAltOutlinedIcon color="primary" />
                        </IconButton>
                    </Tooltip>
                    : null}
            </>
        )
    }

    const canEditQuote = (row: any) =>
        (standardQuote || customQuote || quoteApprover) && row?.status === 1;

    // Interactive cells keep their own click behaviour.
    const handleRowEditClick = (params: any) => {
        if (['status', 'action', 'comments'].includes(params.field)) {
            return;
        }

        if (canEditQuote(params.row)) {
            pagePermissionCheck(params.row, 'Edit');
        } else if ([2, 3, 4].includes(params.row?.status)) {
            pagePreview(params.row.id, 'Preview');
        }
    };

    const columns = [
        {
            field: 'quoteNumber',
            headerName: 'Quote Number',
            minWidth: 150,
            renderCell: renderQuoteNumber
        },
        {
            field: 'clientName',
            headerName: 'Client Name',
            minWidth: 200,
            renderCell: renderClientName
        },
        {
            field: 'projectName',
            headerName: 'Project Name',
            minWidth: 120,
            renderCell: renderProjectName
        },
        {
            field: 'versionNumber',
            headerName: 'Version',
            minWidth: 60,
            maxWidth: 60
        },
        {
            field: 'createdByName',
            headerName: 'Created by',
            minWidth: 100,
            renderCell: renderCreatedByName
        },
        {
            field: 'createdOn',
            headerName: 'Created on',
            minWidth: 100,
            renderCell: dateRender
        },
        {
            field: 'status',
            headerName: 'Status',
            minWidth: 200,
            renderCell: statusRender
        },
        {
            field: 'action',
            headerName: 'Action',
            minWidth: 80,
            maxWidth: 80,
            renderCell: handleAction
        },
        {
            field: 'comments',
            headerName: '',
            minWidth: 60,
            maxWidth: 60,
            renderCell: handleComment
        }
    ];

    let nav: any = [{ name: 'Home', path: '/home/viewQuote' }, { name: 'Quote', path: '/home/viewQuote' }, { name: 'View Quotes', path: '/home/viewQuote' }];

    const confirmQuoteNumber = confirmAction === 'clone'
        ? pendingCloneItem?.quoteNumber
        : confirmAction === 'approve' || confirmAction === 'reject'
            ? statusObj?.quoteNumber
            : selectedItem?.quoteNumber;

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', cursor: 'pointer' }}>
                <Breadcrumb navList={nav} />
            </div>
            <Typography variant="h6" data-testid="view-heading" gutterBottom align='left' mt={2} mb={2}>
                View Quotes
            </Typography>
            <QuoteSearchForm onSearch={handleSearch} onClear={handleClear} />
            <Grid container spacing={2}>
                <Grid item xs={12} sm={12} md={12}>
                    <DataGrid
                        autoHeight
                        sx={{
                            '.MuiDataGrid-columnHeaders': {
                                backgroundColor: '#003883',
                                color: '#fff'
                            },
                            '.MuiDataGrid-menuIcon .MuiSvgIcon-root': {
                                color: '#fff'
                            },
                            '.MuiDataGrid-iconButtonContainer .MuiSvgIcon-root': {
                                color: '#fff'
                            },
                            '.quote-row-clickable': {
                                cursor: 'pointer'
                            }
                        }}
                        columns={columns}
                        rows={paginationModel.data}
                        rowCount={paginationModel.total}
                        loading={paginationModel.isLoading}
                        page={paginationModel.page - 1}
                        pageSize={paginationModel.pageSize}
                        pagination
                        paginationMode="server"
                        onCellClick={handleRowEditClick}
                        getRowClassName={() => 'quote-row-clickable'}
                        onPageChange={(newPage: any) => {
                            setPaginationModel(old => ({ ...old, page: newPage + 1 }))
                        }}
                    />
                </Grid>
            </Grid>
            <SharedQuotes
                open={isSharedQuoteEnable}
                onClose={() => setIsSharedQuoteEnable(false)}
                sharedQuoteNumber={sharedQuoteNumber}
                sharedQuoteStatus={true}
                isShowAllPopup={isShowAllPopup}
                currentRowItem={currentRowItem}
                isSuccessMsg={isSuccessMsg}
            />
            <ShareWithCustomer
                open={isShareWithCustomer}
                onClose={() => setIsShareWithCustomer(false)}
                userInfo={userInfo}
                shareWithCustomer={shareWithCustomer}
                handleShareWithCustomer={handleShareWithCustomer}
                isSuccessMsg={isSuccessMsg}
            />
            {confirmOpen && <QuoteConfirmationDialog
                onClose={() => setConfirmOpen(false)}
                fullScreen={fullScreen}
                title={<>
                        Are you sure you want to {confirmAction === 'approve' ? 'approve' : confirmAction === 'reject' ? 'reject' : confirmAction === 'clone' ? (pendingCloneType === 'version' ? 'Create New Revision for' : 'clone') : 'delete'} <Box component="span" sx={{ fontWeight: 700 }}>{confirmQuoteNumber}</Box>?
                </>}
                commentLabel={confirmAction === 'reject' ? 'Rejection Reason' : confirmAction === 'approve' ? 'Comments' : undefined}
                initialComment={confirmAction === 'approve' ? commentsValue : ''}
                onConfirm={submitClick}
                onCancel={() => {
                    setConfirmOpen(false);
                    setAnchorEl(null);
                }}
            />}
            <CommentsGrid
                open={openComments}
                onClose={() => {
                    setOpenComments(false);
                }}
                commentList={commentList}
            />
            <Stack sx={{ width: '100%', marginTop: 2 }} spacing={2}>
                <Snackbar open={duplicateError} autoHideDuration={3000} onClose={() => setDuplicateError(false)}>
                    <Alert severity="warning" onClose={() => setDuplicateError(false)}> User doesn't have required permission to Update.</Alert>
                </Snackbar>
                <Snackbar open={quoteNotExist} autoHideDuration={3000} onClose={() => setQuoteNotExist(false)}>
                    <Alert severity="warning" onClose={() => setQuoteNotExist(false)}> Quote Id doesn't exist</Alert>
                </Snackbar>
                <Snackbar open={deleteQuoteStatus} autoHideDuration={3000} onClose={() => setDeleteQuoteStatus(false)}>
                    <Alert severity="success" onClose={() => setDeleteQuoteStatus(false)}> Deleted Quote Successfully!</Alert>
                </Snackbar>
                <Snackbar open={isSuccessMsg} autoHideDuration={3000} onClose={() => setIsSuccessMsg(false)}>
                    <Alert severity="success" onClose={() => setIsSuccessMsg(false)}> Mail Sent Successfully!</Alert>
                </Snackbar>
                <Snackbar open={successStatusUpdate.open} autoHideDuration={3000} onClose={() => setSuccessStatusUpdate({ ...successStatusUpdate, open: false })}>
                    <Alert severity={successStatusUpdate.type} onClose={() => setSuccessStatusUpdate({ ...successStatusUpdate, open: false })}><b>{successStatusUpdate.message}</b></Alert>
                </Snackbar>
                <Snackbar open={internalError} autoHideDuration={3000} onClose={() => setInternalError(false)}>
                    <Alert severity="error" onClose={() => setInternalError(false)}> Please try after some time</Alert>
                </Snackbar>
                <Snackbar open={downloadSuccess} autoHideDuration={3000} onClose={() => setDownloadSuccess(false)}>
                    <Alert severity="success" onClose={() => setDownloadSuccess(false)}>Downloading the report!</Alert>
                </Snackbar>
                <Snackbar open={commonError.open} autoHideDuration={3000} onClose={() => setCommonError({ open: false, message: '' })}>
                    <Alert severity="error" onClose={() => setCommonError({ open: false, message: '' })}><b>{commonError.message}</b></Alert>
                </Snackbar>
            </Stack>
        </div >
    );
}
